import React, { useEffect, useCallback, memo } from 'react';
import { useGameState } from '@/contexts/GameStateContext';

/**
 * AchievementToast Component - Chrome Native Style with Social Share Hook
 * Memoized to prevent re-renders during game loop
 */

const ACHIEVEMENTS: Record<string, { name: string; description: string }> = {
  'first_game': { name: 'First Steps', description: 'Play your first game' },
  'score_100': { name: 'Century Club', description: 'Reach 100 points' },
  'score_500': { name: 'High Flyer', description: 'Reach 500 points' },
  'score_1000': { name: 'Legendary', description: 'Reach 1000 points' },
  'coins_50': { name: 'Coin Collector', description: 'Earn 50 coins' },
  'coins_200': { name: 'Rich Player', description: 'Earn 200 coins' },
  'perfect_jump': { name: 'Perfect Timing', description: 'Jump at exact moment' },
  'hard_mode': { name: 'Brave Soul', description: 'Beat hard mode' },
};

const SITE_URL = 'https://chromedinogame.com';

export const AchievementToast = memo(() => {
  const { showAchievementToast, setShowAchievementToast, currentAchievement } = useGameState();

  useEffect(() => {
    if (showAchievementToast) {
      const timer = setTimeout(() => {
        setShowAchievementToast(false);
      }, 5000); // Extended to 5s to give time to share
      return () => clearTimeout(timer);
    }
  }, [showAchievementToast, setShowAchievementToast]);

  const handleShare = useCallback(() => {
    if (!currentAchievement) return;
    const achievement = ACHIEVEMENTS[currentAchievement];
    if (!achievement) return;

    const text = `🦖 Just unlocked "${achievement.name}" on the Chrome Dino Game! ${achievement.description}. Can you beat me?`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(SITE_URL)}&hashtags=ChromeDinoGame,TRexRunner`;
    window.open(url, '_blank', 'width=550,height=420,noopener,noreferrer');
  }, [currentAchievement]);

  if (!showAchievementToast || !currentAchievement) {
    return null;
  }

  const achievement = ACHIEVEMENTS[currentAchievement];

  if (!achievement) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: '68px',
        right: '16px',
        zIndex: 10000,
        background: 'var(--bg-color)',
        border: '1px solid #d3d3d3',
        padding: '12px 16px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        fontFamily: 'system-ui, -apple-system, Roboto, Arial, sans-serif',
        fontSize: '13px',
        maxWidth: '320px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ fontSize: '20px' }}>★</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 500, color: '#222222', marginBottom: '2px' }}>
            {achievement.name}
          </div>
          <div style={{ fontSize: '12px', color: '#666666' }}>
            {achievement.description}
          </div>
        </div>
      </div>

      <button
        onClick={handleShare}
        style={{
          background: '#f5f5f5',
          border: '1px solid #d3d3d3',
          color: '#222222',
          padding: '6px 12px',
          fontSize: '12px',
          fontFamily: 'inherit',
          fontWeight: 500,
          cursor: 'pointer',
          transition: 'background 0.1s',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = '#eeeeee';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = '#f5f5f5';
        }}
        aria-label="Share your achievement on Twitter"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
        Share Your Score
      </button>
    </div>
  );
});

AchievementToast.displayName = 'AchievementToast';
